"use client";

import { useQuery } from "@urql/next";
import { graphql, type ResultOf } from "gql.tada";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { AnalysisTarget } from "@/lib/ai/analysis-target";
import type { CurrentPageContext } from "@/lib/ai/current-page-context";
import type { AnalysisTargetOption } from "./analysis-target-options";
import {
  type AnalysisTargetPathHint,
  type CurrentPagePathContext,
  getAnalysisTargetLookupIdCandidates,
  getAnalysisTargetPathHint,
  getAnalysisTargetPathHintSyncKey,
  getAnalysisTargetPathSyncKey,
  getCurrentPagePathContext,
  isSameAnalysisTargetFallbackId,
  isSameAnalysisTargetId,
  resolveAnalysisTargetFromPathHint,
} from "./analysis-target-path";

type UseAnalysisTargetPageContextSyncParams = {
  /**
   * `false` の間は query/effect を購読せず、親 owner へ同期責務を譲る。
   */
  shouldSyncPageContext?: boolean;
  pathname: string;
  options: AnalysisTargetOption[];
  selectedTargets: AnalysisTarget[];
  setPageSelectedTarget: (target: AnalysisTarget | null) => void;
};

type PathSyncSnapshot = {
  pathSyncKey: string | null;
  resolvedTarget: AnalysisTarget | null;
  resolvedTargetSignature: string;
};

type AnalysisTargetPathContext = {
  pathHint: AnalysisTargetPathHint | null;
  pathSyncKey: string | null;
};

type CurrentPageSnapshot = {
  pageKind: CurrentPageContext["pageKind"];
  pathSyncKey: string | null;
  resolvedTarget: AnalysisTarget | null;
  resolvedTargetSignature: string;
};

type AnalysisTargetResolutionInput = {
  pathHint: AnalysisTargetPathHint | null;
  pathSyncKey: string | null;
  previousSync: PathSyncSnapshot | null;
  options: AnalysisTargetOption[];
  selectedTargets: AnalysisTarget[];
  detailNameNodes: Array<AnalysisTargetDetailNameNode | null | undefined>;
  resolvedTargetCache: Map<string, AnalysisTarget>;
};

type AnalysisTargetResolutionResult = {
  resolvedTarget: AnalysisTarget | null;
  resolvedTargetSignature: string;
  cacheableTarget: AnalysisTarget | null;
};

const AnalysisTargetDetailNameQuery = graphql(`
  query AnalysisTargetDetailName($id: ID!) {
    node(id: $id) {
      __typename
      ... on SearchAnalyticsView {
        id
        name
      }
      ... on UserAnalyticsView {
        id
        name
      }
      ... on IndexWorker {
        id
        name
      }
    }
  }
`);

type AnalysisTargetDetailNameQueryResult = ResultOf<
  typeof AnalysisTargetDetailNameQuery
>;
type AnalysisTargetDetailNameNode = AnalysisTargetDetailNameQueryResult["node"];
type ResolvableAnalysisTargetDetailNameNode = Extract<
  AnalysisTargetDetailNameNode,
  {
    __typename: "SearchAnalyticsView" | "UserAnalyticsView" | "IndexWorker";
  }
>;

/**
 * 画面遷移で hook インスタンスが再生成されても、同一キーの安定ラベルを再利用する共有キャッシュ。
 */
const sharedResolvedTargetCache = new Map<string, AnalysisTarget>();

/**
 * テスト実行間の共有キャッシュ汚染を防ぐためのリセット関数。
 */
export function __resetSharedAnalysisTargetPageContextCacheForTest(): void {
  sharedResolvedTargetCache.clear();
}

/**
 * App Router の内部遷移直後は `pathname` prop が一時的に古いことがあるため、
 * browser pathname 同期は 1 tick 遅延させて実行する。
 */
const BROWSER_PATHNAME_SYNC_DEFER_MS = 0;
function isResolvableAnalysisTargetDetailNameNode(
  detailNode: AnalysisTargetDetailNameNode | null | undefined,
): detailNode is ResolvableAnalysisTargetDetailNameNode {
  return (
    detailNode?.__typename === "SearchAnalyticsView" ||
    detailNode?.__typename === "UserAnalyticsView" ||
    detailNode?.__typename === "IndexWorker"
  );
}

function readCurrentBrowserPathname(pathnameFallback: string): string {
  if (typeof window === "undefined") {
    return pathnameFallback;
  }
  const browserPathname = window.location.pathname;
  if (!browserPathname || browserPathname === "/") {
    return pathnameFallback;
  }
  return browserPathname;
}

function toProjectHintSyncKey(
  hint: AnalysisTargetPathHint | null,
): string | null {
  if (!hint) {
    return null;
  }
  return getAnalysisTargetPathHintSyncKey({
    kind: "project",
    projectType: hint.projectType,
    projectId: hint.projectId,
  });
}

function selectPathContext(
  browserPathname: string,
  propPathname: string,
): AnalysisTargetPathContext {
  const browserPathHint = getAnalysisTargetPathHint(browserPathname);
  const propPathHint = getAnalysisTargetPathHint(propPathname);

  if (!browserPathHint) {
    return {
      pathHint: null,
      pathSyncKey: null,
    };
  }
  if (!propPathHint) {
    return {
      pathHint: browserPathHint,
      pathSyncKey: getAnalysisTargetPathHintSyncKey(browserPathHint),
    };
  }

  const browserProjectKey = toProjectHintSyncKey(browserPathHint);
  const propProjectKey = toProjectHintSyncKey(propPathHint);
  const isSameProject =
    browserProjectKey !== null &&
    propProjectKey !== null &&
    browserProjectKey === propProjectKey;
  const shouldPreferPropDetailHint =
    isSameProject &&
    browserPathHint.kind !== propPathHint.kind &&
    browserPathHint.kind !== "detail";
  const preferredPathHint = shouldPreferPropDetailHint
    ? propPathHint
    : browserPathHint;

  return {
    pathHint: preferredPathHint,
    pathSyncKey: getAnalysisTargetPathHintSyncKey(preferredPathHint),
  };
}

function selectCurrentPagePathContext(
  browserPathname: string,
  propPathname: string,
): CurrentPagePathContext | null {
  const browserContext = getCurrentPagePathContext(browserPathname);
  const propContext = getCurrentPagePathContext(propPathname);

  if (!browserContext) {
    return propContext;
  }
  if (!propContext) {
    return browserContext;
  }

  const browserProjectKey = toProjectHintSyncKey(browserContext.targetHint);
  const propProjectKey = toProjectHintSyncKey(propContext.targetHint);
  const isSameProject =
    browserProjectKey !== null &&
    propProjectKey !== null &&
    browserProjectKey === propProjectKey;
  if (isSameProject && browserContext.pageKind !== propContext.pageKind) {
    return propContext;
  }

  return browserContext;
}

function getAnalysisTargetSyncSignature(target: AnalysisTarget | null): string {
  if (!target) {
    return "__null__";
  }
  return [
    target.projectType,
    target.projectId,
    target.projectName,
    target.viewId ?? "",
    target.viewName ?? "",
    target.iwId ?? "",
    target.iwName ?? "",
  ].join("|");
}

function toProjectPathHintFromTarget(
  target: AnalysisTarget | null,
): AnalysisTargetPathHint | null {
  if (!target) {
    return null;
  }
  return {
    kind: "project",
    projectType: target.projectType,
    projectId: target.projectId,
  };
}

function toProjectOnlyTarget(target: AnalysisTarget): AnalysisTarget {
  return {
    projectType: target.projectType,
    projectId: target.projectId,
    projectName: target.projectName,
  };
}

function getDetailIdCandidates(
  pathHint: AnalysisTargetPathHint | null,
): string[] {
  if (!pathHint || pathHint.kind !== "detail") {
    return [];
  }
  const detailId = pathHint.viewId ?? pathHint.iwId;
  return detailId ? getAnalysisTargetLookupIdCandidates(detailId) : [];
}

function getPreferredDetailNameNode(
  pathHint: AnalysisTargetPathHint | null,
  detailNodes: Array<AnalysisTargetDetailNameNode | null | undefined>,
): ResolvableAnalysisTargetDetailNameNode | null {
  if (!pathHint || pathHint.kind !== "detail") {
    return null;
  }
  const detailId = pathHint.viewId ?? pathHint.iwId;
  if (!detailId) {
    return null;
  }

  return (
    detailNodes.find(
      (detailNode): detailNode is ResolvableAnalysisTargetDetailNameNode =>
        isResolvableAnalysisTargetDetailNameNode(detailNode) &&
        isSameAnalysisTargetId(detailNode.id, detailId),
    ) ?? null
  );
}

function resolveAnalysisTargetFromDetailNode(
  pathHint: AnalysisTargetPathHint | null,
  scopedTarget: AnalysisTarget | null,
  detailNode: ResolvableAnalysisTargetDetailNameNode | null | undefined,
): AnalysisTarget | null {
  if (!pathHint || pathHint.kind !== "detail" || !scopedTarget || !detailNode) {
    return null;
  }
  const detailId = pathHint.viewId ?? pathHint.iwId;
  if (!detailId || !isSameAnalysisTargetId(detailNode.id, detailId)) {
    return null;
  }

  if (pathHint.viewId) {
    if (
      pathHint.projectType === "SearchAnalyticsProject" &&
      detailNode.__typename === "SearchAnalyticsView"
    ) {
      return {
        ...scopedTarget,
        viewId: pathHint.viewId,
        viewName: detailNode.name,
      };
    }
    if (
      pathHint.projectType === "UserAnalyticsProject" &&
      detailNode.__typename === "UserAnalyticsView"
    ) {
      return {
        ...scopedTarget,
        viewId: pathHint.viewId,
        viewName: detailNode.name,
      };
    }
    return null;
  }

  if (
    pathHint.iwId &&
    pathHint.projectType === "IndexWorkerProject" &&
    detailNode.__typename === "IndexWorker"
  ) {
    return {
      ...scopedTarget,
      iwId: pathHint.iwId,
      iwName: detailNode.name,
    };
  }

  return null;
}

function isFallbackLabel(
  label: string | undefined,
  fallbackId: string | undefined,
): boolean {
  if (!label || label.trim().length === 0) {
    return true;
  }
  if (!fallbackId) {
    return false;
  }
  return isSameAnalysisTargetFallbackId(label.trim(), fallbackId);
}

function hasStableProjectLabel(target: AnalysisTarget): boolean {
  return !isFallbackLabel(target.projectName, target.projectId);
}

function hasStableDetailLabel(target: AnalysisTarget): boolean {
  if (target.viewId) {
    return !isFallbackLabel(target.viewName, target.viewId);
  }
  if (target.iwId) {
    return !isFallbackLabel(target.iwName, target.iwId);
  }
  return true;
}

function shouldCacheResolvedTarget(target: AnalysisTarget): boolean {
  if (target.viewId || target.iwId) {
    return hasStableDetailLabel(target);
  }
  return hasStableProjectLabel(target);
}

function mergeKnownLabelsForSamePath(
  target: AnalysisTarget,
  preferredCandidates: AnalysisTarget[],
  pathSyncKey: string | null,
): AnalysisTarget {
  if (!pathSyncKey || getAnalysisTargetPathSyncKey(target) !== pathSyncKey) {
    return target;
  }

  return preferredCandidates.reduce((mergedTarget, candidate) => {
    if (getAnalysisTargetPathSyncKey(candidate) !== pathSyncKey) {
      return mergedTarget;
    }

    let nextTarget = mergedTarget;
    if (
      isFallbackLabel(nextTarget.projectName, nextTarget.projectId) &&
      hasStableProjectLabel(candidate)
    ) {
      nextTarget = { ...nextTarget, projectName: candidate.projectName };
    }

    if (
      nextTarget.viewId &&
      candidate.viewId &&
      isFallbackLabel(nextTarget.viewName, nextTarget.viewId) &&
      !isFallbackLabel(candidate.viewName, candidate.viewId)
    ) {
      nextTarget = { ...nextTarget, viewName: candidate.viewName };
    }

    if (
      nextTarget.iwId &&
      candidate.iwId &&
      isFallbackLabel(nextTarget.iwName, nextTarget.iwId) &&
      !isFallbackLabel(candidate.iwName, candidate.iwId)
    ) {
      nextTarget = { ...nextTarget, iwName: candidate.iwName };
    }

    return nextTarget;
  }, target);
}

function buildPathScopedAnalysisTarget(
  pathHint: AnalysisTargetPathHint,
  pathSyncKey: string | null,
  previousSync: PathSyncSnapshot | null,
  cachedTarget: AnalysisTarget | null,
  cachedProjectTarget: AnalysisTarget | null,
): AnalysisTarget {
  const previousTarget = previousSync?.resolvedTarget ?? null;
  const currentProjectSyncKey = getAnalysisTargetPathHintSyncKey({
    kind: "project",
    projectType: pathHint.projectType,
    projectId: pathHint.projectId,
  });
  const previousProjectSyncKey = getAnalysisTargetPathHintSyncKey(
    toProjectPathHintFromTarget(previousTarget),
  );
  const shouldReusePreviousProjectName =
    currentProjectSyncKey !== null &&
    previousProjectSyncKey !== null &&
    currentProjectSyncKey === previousProjectSyncKey &&
    Boolean(previousTarget?.projectName);
  const projectName = shouldReusePreviousProjectName
    ? (previousTarget?.projectName ?? pathHint.projectId)
    : (cachedProjectTarget?.projectName ??
      cachedTarget?.projectName ??
      pathHint.projectId);

  if (pathHint.kind === "project") {
    return {
      projectType: pathHint.projectType,
      projectId: pathHint.projectId,
      projectName,
    };
  }

  if (pathHint.iwId) {
    const cachedIwTarget = cachedTarget?.iwId ? cachedTarget : null;
    const previousIwId =
      previousSync?.pathSyncKey === pathSyncKey
        ? (previousTarget?.iwId ?? null)
        : null;
    const iwId = previousIwId ?? cachedIwTarget?.iwId ?? pathHint.iwId;
    const iwName =
      previousIwId && previousTarget?.iwName
        ? previousTarget.iwName
        : (cachedIwTarget?.iwName ?? pathHint.iwId);
    return {
      projectType: pathHint.projectType,
      projectId: pathHint.projectId,
      projectName,
      iwId,
      iwName,
    };
  }

  if (pathHint.viewId) {
    const cachedViewTarget = cachedTarget?.viewId ? cachedTarget : null;
    const previousViewId =
      previousSync?.pathSyncKey === pathSyncKey
        ? (previousTarget?.viewId ?? null)
        : null;
    const viewId =
      previousViewId ?? cachedViewTarget?.viewId ?? pathHint.viewId;
    const viewName =
      previousViewId && previousTarget?.viewName
        ? previousTarget.viewName
        : (cachedViewTarget?.viewName ?? pathHint.viewId);
    return {
      projectType: pathHint.projectType,
      projectId: pathHint.projectId,
      projectName,
      viewId,
      viewName,
    };
  }

  return {
    projectType: pathHint.projectType,
    projectId: pathHint.projectId,
    projectName,
  };
}

function resolvePageTarget({
  pathHint,
  pathSyncKey,
  previousSync,
  options,
  selectedTargets,
  detailNameNodes,
  resolvedTargetCache,
}: AnalysisTargetResolutionInput): AnalysisTargetResolutionResult {
  let resolvedTarget: AnalysisTarget | null = null;
  let resolvedFromOptions: AnalysisTarget | null = null;
  let resolvedFromDetailNode: AnalysisTarget | null = null;

  if (pathHint) {
    const cachedTarget =
      pathSyncKey !== null
        ? (resolvedTargetCache.get(pathSyncKey) ?? null)
        : null;
    const projectSyncKey = toProjectHintSyncKey(pathHint);
    const cachedProjectTarget =
      projectSyncKey !== null
        ? (resolvedTargetCache.get(projectSyncKey) ?? null)
        : null;

    const scopedTarget = buildPathScopedAnalysisTarget(
      pathHint,
      pathSyncKey,
      previousSync,
      cachedTarget,
      cachedProjectTarget,
    );
    resolvedFromOptions = resolveAnalysisTargetFromPathHint(pathHint, options);
    resolvedFromDetailNode = resolveAnalysisTargetFromDetailNode(
      pathHint,
      scopedTarget,
      getPreferredDetailNameNode(pathHint, detailNameNodes),
    );
    resolvedTarget =
      resolvedFromOptions ?? resolvedFromDetailNode ?? scopedTarget;
  }

  const preferredLabelCandidates = [
    previousSync?.resolvedTarget,
    ...selectedTargets,
  ].filter((candidate): candidate is AnalysisTarget => candidate !== null);

  if (resolvedTarget) {
    resolvedTarget = mergeKnownLabelsForSamePath(
      resolvedTarget,
      preferredLabelCandidates,
      pathSyncKey,
    );
  }
  if (resolvedFromOptions) {
    resolvedFromOptions = mergeKnownLabelsForSamePath(
      resolvedFromOptions,
      preferredLabelCandidates,
      pathSyncKey,
    );
  }
  if (resolvedFromDetailNode) {
    resolvedFromDetailNode = mergeKnownLabelsForSamePath(
      resolvedFromDetailNode,
      preferredLabelCandidates,
      pathSyncKey,
    );
  }

  return {
    resolvedTarget,
    resolvedTargetSignature: getAnalysisTargetSyncSignature(resolvedTarget),
    cacheableTarget: resolvedFromOptions ?? resolvedFromDetailNode,
  };
}

function cacheResolvedTarget(params: {
  pathSyncKey: string | null;
  cacheableTarget: AnalysisTarget | null;
  resolvedTargetCache: Map<string, AnalysisTarget>;
}): void {
  const { pathSyncKey, cacheableTarget, resolvedTargetCache } = params;
  if (
    !pathSyncKey ||
    !cacheableTarget ||
    !shouldCacheResolvedTarget(cacheableTarget)
  ) {
    return;
  }

  resolvedTargetCache.set(pathSyncKey, cacheableTarget);
  const projectHint = toProjectPathHintFromTarget(cacheableTarget);
  const projectSyncKey = getAnalysisTargetPathHintSyncKey(projectHint);
  if (projectSyncKey && hasStableProjectLabel(cacheableTarget)) {
    resolvedTargetCache.set(
      projectSyncKey,
      toProjectOnlyTarget(cacheableTarget),
    );
  }
}

function buildCurrentPageContext(params: {
  pageKind: CurrentPageContext["pageKind"] | null;
  resolvedTarget: AnalysisTarget | null;
}): CurrentPageContext | null {
  const { pageKind, resolvedTarget } = params;
  if (!pageKind) {
    return null;
  }

  return {
    pageKind,
    target: resolvedTarget,
  } satisfies CurrentPageContext;
}

/**
 * URL と候補データから、現在ページの分析対象コンテキストを同期する。
 *
 * 契約:
 * - `currentPageContext` は actual page identity として常に更新する
 * - page chip への自動同期は suppression 中の path key では再適用しない
 * - label は `options -> detail node -> cache -> fallback` の優先順で補完する
 */
export function useAnalysisTargetPageContextSync({
  shouldSyncPageContext = true,
  pathname,
  options,
  selectedTargets,
  setPageSelectedTarget,
}: UseAnalysisTargetPageContextSyncParams): {
  currentPageContext: CurrentPageContext | null;
  suppressPageContextAutoSyncOnCurrentPath: () => void;
} {
  const [observedBrowserPathname, setObservedBrowserPathname] =
    useState(pathname);
  const [currentPageContext, setCurrentPageContext] =
    useState<CurrentPageContext | null>(null);
  const pathnameFallbackRef = useRef(pathname);
  const browserPathSyncTimerRef = useRef<number | null>(null);
  const isBrowserPathSyncScheduledRef = useRef(false);
  const lastPathSyncRef = useRef<PathSyncSnapshot | null>(null);
  const lastCurrentPageSyncRef = useRef<CurrentPageSnapshot | null>(null);
  const resolvedTargetCacheRef = useRef(sharedResolvedTargetCache);
  const suppressedPathSyncKeyRef = useRef<string | null | undefined>(undefined);

  const syncObservedPathname = useCallback(() => {
    if (!shouldSyncPageContext) {
      return;
    }
    const nextPathname = readCurrentBrowserPathname(
      pathnameFallbackRef.current,
    );
    setObservedBrowserPathname((previousPathname) =>
      previousPathname === nextPathname ? previousPathname : nextPathname,
    );
  }, [shouldSyncPageContext]);

  const scheduleObservedPathnameSync = useCallback(() => {
    if (
      !shouldSyncPageContext ||
      typeof window === "undefined" ||
      isBrowserPathSyncScheduledRef.current
    ) {
      return;
    }
    isBrowserPathSyncScheduledRef.current = true;
    browserPathSyncTimerRef.current = window.setTimeout(() => {
      isBrowserPathSyncScheduledRef.current = false;
      browserPathSyncTimerRef.current = null;
      syncObservedPathname();
    }, BROWSER_PATHNAME_SYNC_DEFER_MS);
  }, [shouldSyncPageContext, syncObservedPathname]);

  useEffect(() => {
    pathnameFallbackRef.current = pathname;
    if (!shouldSyncPageContext) {
      return;
    }
    syncObservedPathname();
  }, [pathname, shouldSyncPageContext, syncObservedPathname]);

  useEffect(() => {
    if (!shouldSyncPageContext || typeof window === "undefined") {
      return;
    }

    // App Router の通常遷移は `pathname` prop（usePathname）を正とし、
    // browser back/forward だけ native popstate で補完する。
    // query/hash-only の History API 更新は「現在ページ」を変えないため監視しない。
    const syncPathnameAfterBrowserNavigation = () => {
      scheduleObservedPathnameSync();
    };
    window.addEventListener("popstate", syncPathnameAfterBrowserNavigation);
    scheduleObservedPathnameSync();

    return () => {
      window.removeEventListener(
        "popstate",
        syncPathnameAfterBrowserNavigation,
      );
      if (browserPathSyncTimerRef.current !== null) {
        window.clearTimeout(browserPathSyncTimerRef.current);
        browserPathSyncTimerRef.current = null;
      }
      isBrowserPathSyncScheduledRef.current = false;
    };
  }, [scheduleObservedPathnameSync, shouldSyncPageContext]);

  const pathContext = useMemo(
    () => selectPathContext(observedBrowserPathname, pathname),
    [observedBrowserPathname, pathname],
  );
  const currentPagePathContext = useMemo(
    () => selectCurrentPagePathContext(observedBrowserPathname, pathname),
    [observedBrowserPathname, pathname],
  );
  const detailIdCandidatesForLabelLookup = useMemo(
    () =>
      getDetailIdCandidates(
        currentPagePathContext?.targetHint ?? pathContext.pathHint,
      ),
    [currentPagePathContext?.targetHint, pathContext.pathHint],
  );

  const detailPrimaryLookupId = detailIdCandidatesForLabelLookup[0] ?? null;
  const detailSecondaryLookupId = detailIdCandidatesForLabelLookup[1] ?? null;
  const [{ data: primaryDetailNameData }] = useQuery({
    query: AnalysisTargetDetailNameQuery,
    variables: {
      id: detailPrimaryLookupId ?? "",
    },
    pause: !shouldSyncPageContext || detailPrimaryLookupId === null,
    requestPolicy: "cache-and-network",
  });
  const [{ data: secondaryDetailNameData }] = useQuery({
    query: AnalysisTargetDetailNameQuery,
    variables: {
      id: detailSecondaryLookupId ?? "",
    },
    pause: !shouldSyncPageContext || detailSecondaryLookupId === null,
    requestPolicy: "cache-and-network",
  });

  const suppressPageContextAutoSyncOnCurrentPath = useCallback(() => {
    if (!shouldSyncPageContext) {
      return;
    }
    suppressedPathSyncKeyRef.current = pathContext.pathSyncKey;
    lastPathSyncRef.current = null;
  }, [pathContext.pathSyncKey, shouldSyncPageContext]);

  useEffect(() => {
    if (!shouldSyncPageContext) {
      setCurrentPageContext(null);
      lastCurrentPageSyncRef.current = null;
      return;
    }

    if (!currentPagePathContext) {
      setCurrentPageContext(null);
      lastCurrentPageSyncRef.current = null;
      return;
    }

    const lastSync = lastCurrentPageSyncRef.current;
    const resolution = resolvePageTarget({
      pathHint: currentPagePathContext.targetHint,
      pathSyncKey: currentPagePathContext.targetSyncKey,
      previousSync: lastSync,
      options,
      selectedTargets,
      detailNameNodes: [
        primaryDetailNameData?.node,
        secondaryDetailNameData?.node,
      ],
      resolvedTargetCache: resolvedTargetCacheRef.current,
    });
    const nextCurrentPageContext = buildCurrentPageContext({
      pageKind: currentPagePathContext.pageKind,
      resolvedTarget: resolution.resolvedTarget,
    });
    const nextSignature = [
      currentPagePathContext.pageKind,
      resolution.resolvedTargetSignature,
    ].join("|");

    if (
      lastSync?.pageKind === currentPagePathContext.pageKind &&
      lastSync.pathSyncKey === currentPagePathContext.targetSyncKey &&
      lastSync.resolvedTargetSignature === nextSignature
    ) {
      return;
    }

    cacheResolvedTarget({
      pathSyncKey: currentPagePathContext.targetSyncKey,
      cacheableTarget: resolution.cacheableTarget,
      resolvedTargetCache: resolvedTargetCacheRef.current,
    });
    setCurrentPageContext(nextCurrentPageContext);
    lastCurrentPageSyncRef.current = {
      pageKind: currentPagePathContext.pageKind,
      pathSyncKey: currentPagePathContext.targetSyncKey,
      resolvedTarget: resolution.resolvedTarget,
      resolvedTargetSignature: nextSignature,
    };
  }, [
    currentPagePathContext,
    shouldSyncPageContext,
    options,
    primaryDetailNameData?.node,
    secondaryDetailNameData?.node,
    selectedTargets,
  ]);

  useEffect(() => {
    if (!shouldSyncPageContext) {
      return;
    }

    const suppressedPathSyncKey = suppressedPathSyncKeyRef.current;
    if (suppressedPathSyncKey !== undefined) {
      if (suppressedPathSyncKey === pathContext.pathSyncKey) {
        return;
      }
      suppressedPathSyncKeyRef.current = undefined;
    }

    const lastSync = lastPathSyncRef.current;
    const resolution = resolvePageTarget({
      pathHint: pathContext.pathHint,
      pathSyncKey: pathContext.pathSyncKey,
      previousSync: lastSync,
      options,
      selectedTargets,
      detailNameNodes: [
        primaryDetailNameData?.node,
        secondaryDetailNameData?.node,
      ],
      resolvedTargetCache: resolvedTargetCacheRef.current,
    });

    if (
      // 同一 path key で解決署名まで一致していれば、再セットは不要。
      // pathname 自体は比較せず、表示コンテキスト差分だけで更新判定する。
      lastSync?.pathSyncKey === pathContext.pathSyncKey &&
      lastSync.resolvedTargetSignature === resolution.resolvedTargetSignature
    ) {
      return;
    }

    setPageSelectedTarget(resolution.resolvedTarget);

    cacheResolvedTarget({
      pathSyncKey: pathContext.pathSyncKey,
      cacheableTarget: resolution.cacheableTarget,
      resolvedTargetCache: resolvedTargetCacheRef.current,
    });

    lastPathSyncRef.current = {
      pathSyncKey: pathContext.pathSyncKey,
      resolvedTarget: resolution.resolvedTarget,
      resolvedTargetSignature: resolution.resolvedTargetSignature,
    };
  }, [
    options,
    pathContext.pathHint,
    pathContext.pathSyncKey,
    primaryDetailNameData?.node,
    secondaryDetailNameData?.node,
    selectedTargets,
    setPageSelectedTarget,
    shouldSyncPageContext,
  ]);

  return {
    currentPageContext,
    suppressPageContextAutoSyncOnCurrentPath,
  };
}
